/**
 * The chips of *Accesos*, as components rather than inline arrows.
 *
 * They are components for the reason `ProjectDimensionsPane` gives about
 * its own three: while a cell is an inline arrow function inside
 * `columns`, a test can assert everything about the dictionary and
 * NOTHING about the screen. Exported, they render with
 * `react-dom/server`, so a test reads the MARKUP an unlisted value
 * produces — the raw key, the `font-mono` treatment and the warning
 * glyph — and that is the guard the dictionary assertion is not.
 */

import { StatusChip, resolveStatus, type StatusDef } from "@pervasivemind/admin-ui";

import {
  memberScopeDict,
  memberScopeVerdict,
  type TFn,
} from "@/pages/admin-access/access-status";

/** One raw backend word, through a dictionary, as a chip. */
export function DictChip({
  value,
  dictionary,
  t,
  title,
}: {
  value: string | null | undefined;
  dictionary: Record<string, StatusDef>;
  t: TFn;
  /** The backend's own sentence, when there is one. It loses to
   *  `st.title`: "this label is missing" outranks a detail describing a
   *  state the screen just failed to name. */
  title?: string | null;
}): JSX.Element {
  const st = resolveStatus(value, dictionary, t("access.common.missingLabel"));
  return (
    <StatusChip
      label={st.label}
      tone={st.tone}
      unknown={st.unknown}
      title={st.title ?? title ?? undefined}
    />
  );
}

/**
 * A count that never renders a bare number.
 *
 * `n con acceso · m sin acceso` rather than `3`: the second half is the
 * one an analyst needs and a single number hides it. `null` is UNKNOWN
 * and renders as an em-dash with the hint — never as `0`, which on this
 * surface is a real and different answer.
 */
export function AppliesToCell({
  appliesTo,
  total,
  t,
}: {
  appliesTo: number | null;
  total: number | null;
  t: TFn;
}): JSX.Element {
  if (appliesTo === null || total === null) {
    return (
      <span
        className="text-[13px] text-neutral-500"
        title={t("access.common.notComputedHint")}
      >
        {t("access.common.notComputed")}
      </span>
    );
  }
  const without = Math.max(0, total - appliesTo);
  if (appliesTo === 0) {
    return (
      <StatusChip
        label={t("access.scopes.appliesToNone")}
        tone="danger"
        title={t("access.scopes.appliesToNoneHint")}
      />
    );
  }
  return (
    <span className="text-[13px] text-neutral-700">
      {t("access.scopes.appliesTo", { n: appliesTo, m: without })}
    </span>
  );
}

/**
 * What a group member actually resolves to — as a VERDICT on this group.
 *
 * The whole point of the chip is the row it separates from its
 * neighbour. On a restricted group, a member who also sits in an
 * unrestricted one reads every row; the restriction on this page decides
 * nothing for them. Before this cell they rendered exactly like a member
 * whose restriction is the one they read through: same id column, same
 * grey "unión de N ámbitos" underneath, no way to tell the inert from
 * the live without opening each person.
 *
 * So the verdict comes first and the id second. The union count stays,
 * because when the verdict IS a warning the count is the first place to
 * look for why — but it is now a detail under an answer rather than the
 * answer itself.
 *
 * `not_applicable` renders no chip at all (an unrestricted group has no
 * cut to be inert), and that is the one case where absence is honest:
 * there is no difference between these rows to show.
 *
 * `groupScopeId: null` means the group has not loaded, and it also
 * renders no chip — a REFUSAL to judge, not a default. Substituting
 * `all` there would silently turn "we do not know this group's cut" into
 * "this group has no cut", which is the same sentence with the opposite
 * consequence.
 */
export function MemberScopeCell({
  groupScopeId,
  effectiveScopeId,
  groupScopeCount,
  t,
}: {
  /** The scope THIS group carries — the claim the row is judged against.
   *  `null` only while the group itself is still loading. */
  groupScopeId: string | null;
  /** The person's binding, read from `users`, not from this group. */
  effectiveScopeId: string;
  /** How many scopes their groups name in total. `> 1` is what makes the
   *  effective scope a union, and is the explanation for a warning. */
  groupScopeCount: number;
  t: TFn;
}): JSX.Element {
  const verdict =
    groupScopeId === null
      ? "not_applicable"
      : memberScopeVerdict(groupScopeId, effectiveScopeId);
  return (
    <div className="min-w-0 space-y-1">
      {verdict !== "not_applicable" && (
        <DictChip
          value={verdict}
          dictionary={memberScopeDict(t)}
          t={t}
          title={t(`access.groups.members.scope.${verdict}Hint`)}
        />
      )}
      <span className="block truncate font-mono text-[13px] text-neutral-600">
        {effectiveScopeId}
      </span>
      {groupScopeCount > 1 && (
        <span className="block text-[13px] text-neutral-500">
          {t("access.groups.members.union", { n: groupScopeCount })}
        </span>
      )}
    </div>
  );
}

/**
 * A scope's identity: its NAME over its id.
 *
 * The id is `font-mono` and small print because it is a filename, and a
 * derived `eff_…` id is a sha256 prefix that names nothing to a human —
 * so it is never the label. Same cell shape as the Dimensiones screen's
 * `dimension` column, on purpose: two screens about the same objects
 * should not have two ways of writing an identity.
 */
export function ScopeIdentityCell({
  id,
  name,
}: {
  id: string;
  name: string;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="truncate font-medium text-neutral-900">{name}</div>
      <div className="truncate font-mono text-[13px] text-neutral-500">
        {id}
      </div>
    </div>
  );
}
